import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  FormHelperText,
  IconButton,
  InputAdornment,
  Stack
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import Tooltip from '@mui/material/Tooltip';
import ClearIcon from '@mui/icons-material/Clear';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import Divider from '@mui/material/Divider';
import DownloadIcon from '@mui/icons-material/Download';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import { FilePond, registerPlugin } from 'react-filepond';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import 'filepond/dist/filepond.min.css';
import { Controlled as CodeMirror } from 'react-codemirror2';
import 'codemirror/lib/codemirror.css';
import 'codemirror/theme/material.css';
import 'codemirror/mode/groovy/groovy';
import 'codemirror/addon/edit/matchbrackets';
import 'codemirror/addon/edit/closebrackets';
import 'codemirror/addon/fold/foldgutter.css';
import 'codemirror/addon/fold/foldcode';
import 'codemirror/addon/fold/foldgutter';
import 'codemirror/addon/fold/brace-fold';
import 'codemirror/addon/hint/show-hint';
import 'codemirror/addon/hint/show-hint.css';
import '../scripts.css';

import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';

registerPlugin(FilePondPluginFileValidateType);

/**
 * Component for displaying and managing the details of a script. Allows viewing,
 * creating, updating, downloading and uploading Groovy scripts.
 *
 * @component
 *
 * @example
 * <ScriptDetails script={script} onSubmit={handleSubmit} onFieldChange={handleFieldChange} readOnly={readOnly} />
 *
 * @param {Object} props - The properties object.
 * @param {Object} props.script - The script object to be displayed and edited.
 * @param {Function} props.onSubmit - The function to call when the form is submitted.
 * @param {Function} props.onFieldChange - The function to call when the fields value change.
 * @param {boolean} props.readOnly - Whether the form should be in read-only mode.
 *
 * @returns {React.Component} The Script Details component.
 */
function ScriptDetails({ script, onSubmit, onFieldChange, readOnly = false }) {
  const { formatMessage: msg } = useIntl();
  const [files, setFiles] = useState([]);
  const [codeEditorModalOpen, setCodeEditorModalOpen] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty },
    getValues
  } = useForm({
    defaultValues: {
      code: '',
      name: '',
      content: '',
      size: '',
      createdDate: '',
      modifiedDate: '',
      custom: true
    }
  });

  watch();

  useEffect(() => {
    if (onFieldChange) {
      onFieldChange(isDirty);
    }
  }, [isDirty, onFieldChange]);

  const resetScript = useCallback(() => {
    if (script?.code) {
      reset(script);
    } else {
      reset({
        code: '',
        name: '',
        content: '',
        size: '',
        createdDate: '',
        modifiedDate: '',
        custom: true
      });
    }
  }, [script, reset]);

  useEffect(() => {
    resetScript();
  }, [script, resetScript]);

  const clearField = (fieldName) => {
    setValue(fieldName, '');
  };

  const handleReset = () => {
    resetScript();
  };

  // Function to handle opening/closing the code editor modal
  const handleOpenCodeEditorModal = () => setCodeEditorModalOpen(true);
  const handleCloseCodeEditorModal = () => setCodeEditorModalOpen(false);

  // Function to download the script content as a .groovy file
  const handleDownload = () => {
    const { name, content } = getValues();
    const fileName = `${name || 'script'}.groovy`;

    const blob = new Blob([content || ''], { type: 'text/plain;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(link.href);
  };

  const generateScriptCode = (filename) => {
    const baseName = filename.replace(/\.[^/.]+$/, '');
    const normalized = baseName.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const code = normalized
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

    return code;
  };

  const handleFilePondUpdateFiles = (fileItems) => {
    setFiles(fileItems);

    if (fileItems.length > 0) {
      const file = fileItems[0].file;

      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target.result;

        setValue('name', file.name.replace(/\.groovy$/, ''));
        setValue('content', content);
        setValue('size', file.size);
        setValue('code', isCreating ? '' : generateScriptCode(file.name));

        const now = new Date();
        setValue('createdDate', now.toISOString());
        setValue('modifiedDate', now.toISOString());

        onFieldChange(true);
      };

      reader.readAsText(file);
    }
  };

  const isCreating = !script?.code;
  const isEditable = isCreating || (script?.custom && !readOnly);

  // CodeMirror options
  const codeMirrorOptions = {
    mode: 'groovy',
    theme: 'material',
    lineNumbers: true,
    lineWrapping: true,
    matchBrackets: true,
    autoCloseBrackets: true,
    foldGutter: true,
    gutters: ['CodeMirror-linenumbers', 'CodeMirror-foldgutter'],
    readOnly: !isEditable,
    viewportMargin: Infinity,
    indentWithTabs: false,
    tabSize: 2,
    indentUnit: 2,
    extraKeys: {
      'Ctrl-Space': 'autocomplete'
    }
  };

  return (
    <Stack
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      display="flex"
      direction="column"
      sx={{ height: '100%' }}
    >
      <Stack display="flex" direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center">
          <ContentPasteIcon color="text" fontSize="medium" sx={{ mr: '0.75rem' }} />
          <MDTypography variant="h5">
            {msg({
              id: isCreating
                ? 'page.scripts.details.creation.title'
                : 'page.scripts.details.modification.title'
            })}
          </MDTypography>
        </Stack>

        {!isCreating && (
          <Tooltip title={msg({ id: 'action.download' }) || 'Download script'}>
            <IconButton onClick={handleDownload} color="primary" size="medium">
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      <Divider sx={{ mt: '0.5rem', mb: '0.75rem' }} />

      {(isEditable || isCreating) && (
        <Box mb={2}>
          <MDTypography variant="subtitle2" gutterBottom>
            {msg({ id: 'field.script.upload.label' }) || 'Upload Groovy Script'}
          </MDTypography>
          <FilePond
            files={files}
            onupdatefiles={handleFilePondUpdateFiles}
            acceptedFileTypes={['application/x-groovy-script']}
            fileValidateTypeDetectType={(source, type) => {
              if (typeof source === 'string') {
                return null;
              }
              if (source.name && source.name.endsWith('.groovy')) {
                return Promise.resolve('application/x-groovy-script');
              }
              return Promise.resolve(type);
            }}
            allowMultiple={false}
            maxFiles={1}
            name="groovyFile"
            labelIdle={
              msg({ id: 'file.upload.description' }) ||
              'Drag & Drop your Groovy files or <span class="filepond--label-action">Browse</span>'
            }
          />
        </Box>
      )}

      <Stack
        spacing={1}
        display="flex"
        direction="column"
        flex={1}
        overflow="auto"
        mb={2}
        sx={{ scrollbarWidth: 'thin' }}
      >
        {!isCreating && (
          <Controller
            name="code"
            control={control}
            render={({ field }) => (
              <MDInput
                {...field}
                label={msg({ id: 'field.script.code.label' })}
                variant="filled"
                fullWidth
                disabled
                size="small"
                InputProps={{
                  readOnly: true
                }}
              />
            )}
          />
        )}
        <Controller
          name="name"
          control={control}
          rules={{ required: msg({ id: 'field.script.name.required' }) }}
          render={({ field }) => (
            <MDInput
              {...field}
              label={msg({ id: 'field.script.name.label' })}
              variant="filled"
              fullWidth
              required
              disabled={!isEditable || !isCreating}
              error={!!errors.name}
              helperText={errors.name?.message}
              size="small"
              InputProps={{
                endAdornment: isCreating && (
                  <InputAdornment position="end">
                    <Tooltip title={msg({ id: 'tooltip.clear' })}>
                      <IconButton onClick={() => clearField('name')} disabled={!isEditable}>
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
          )}
        />

        {!isCreating && (
          <>
            <Controller
              name="size"
              control={control}
              render={({ field }) => (
                <MDInput
                  {...field}
                  label={msg({ id: 'field.script.size.label' })}
                  variant="filled"
                  fullWidth
                  disabled
                  size="small"
                  InputProps={{
                    readOnly: true,
                    value: field.value ? `${field.value} B` : ''
                  }}
                />
              )}
            />
            <Controller
              name="createdDate"
              control={control}
              render={({ field }) => (
                <MDInput
                  {...field}
                  label={msg({ id: 'field.script.createdDate.label' })}
                  variant="filled"
                  fullWidth
                  disabled
                  size="small"
                  InputProps={{
                    readOnly: true,
                    value: field.value ? new Date(field.value).toLocaleString() : ''
                  }}
                />
              )}
            />
            <Controller
              name="modifiedDate"
              control={control}
              render={({ field }) => (
                <MDInput
                  {...field}
                  label={msg({ id: 'field.script.modifiedDate.label' })}
                  variant="filled"
                  fullWidth
                  disabled
                  size="small"
                  InputProps={{
                    readOnly: true,
                    value: field.value ? new Date(field.value).toLocaleString() : ''
                  }}
                />
              )}
            />
          </>
        )}

        <Controller
          name="content"
          control={control}
          rules={{ required: msg({ id: 'field.script.content.required' }) }}
          render={({ field }) => (
            <div
              style={{
                border: errors.content ? '1px solid #d32f2f' : '1px solid rgba(0, 0, 0, 0.23)',
                borderRadius: '4px'
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <MDTypography variant="subtitle2" gutterBottom>
                  {msg({ id: 'field.script.content.label' })}
                </MDTypography>
                {isEditable && (
                  <Tooltip title={msg({ id: 'tooltip.textarea.expand' }) || 'Expand editor'}>
                    <IconButton onClick={handleOpenCodeEditorModal} color="primary" size="small">
                      <AspectRatioIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>

              <div style={{ height: '400px', fontSize: '0.8rem' }}>
                <CodeMirror
                  value={field.value || ''}
                  options={codeMirrorOptions}
                  onBeforeChange={(editor, data, value) => {
                    field.onChange(value);
                  }}
                />
              </div>

              {errors.content && <FormHelperText error>{errors.content.message}</FormHelperText>}
            </div>
          )}
        />
      </Stack>
      <Divider sx={{ mt: 0 }} />
      <Stack direction="row" spacing={2} sx={{ marginTop: 'auto !important', pt: 2 }}>
        <MDButton
          type="button"
          fullWidth
          color="secondary"
          onClick={handleReset}
          disabled={!isEditable}
        >
          {msg({ id: 'button.reset' })}
        </MDButton>
        <MDButton type="submit" fullWidth color="primary" disabled={!isEditable}>
          {isCreating ? msg({ id: 'button.create' }) : msg({ id: 'button.update' })}
        </MDButton>
      </Stack>

      {/* Full-screen code editor modal */}
      <Dialog
        open={codeEditorModalOpen}
        onClose={handleCloseCodeEditorModal}
        maxWidth="xl"
        fullWidth
      >
        <DialogContent sx={{ pb: 1, pt: 3 }}>
          <Controller
            name="content"
            control={control}
            render={({ field }) => (
              <div style={{ height: '70vh' }}>
                <CodeMirror
                  value={field.value || ''}
                  options={{
                    ...codeMirrorOptions,
                    lineNumbers: true,
                    lineWrapping: true
                  }}
                  onBeforeChange={(editor, data, value) => {
                    field.onChange(value);
                  }}
                />
              </div>
            )}
          />
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCloseCodeEditorModal} color="secondary">
            {msg({ id: 'button.close' }) || 'Close'}
          </MDButton>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

ScriptDetails.propTypes = {
  script: PropTypes.shape({
    code: PropTypes.string,
    name: PropTypes.string,
    content: PropTypes.string,
    size: PropTypes.number,
    createdDate: PropTypes.string,
    modifiedDate: PropTypes.string,
    custom: PropTypes.bool
  }),
  onSubmit: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool
};

export default ScriptDetails;
